import React, { useState, useEffect } from "react";
import { Layout } from "@/components/layout/Layout";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/components/ui/tabs";
import { 
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { initializeStorage } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Upload, UserCircle } from "lucide-react";
import OnboardingTab from "@/components/onboarding/OnboardingTab";

const Settings = () => {
  const { isAdmin, user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  
  // User profile form
  const profileForm = useForm({
    defaultValues: {
      name: user?.name || "",
      email: user?.email || "",
    }
  });

  // Notification preferences form
  const notificationForm = useForm({
    defaultValues: {
      emailNotifications: true,
      marketingEmails: false,
      newFeatureAlerts: true
    }
  });

  // Admin settings form (only for admin users)
  const adminSettingsForm = useForm({
    defaultValues: {
      storageEnabled: true,
      maxUploadSizeMB: 500,
      aiGenerationEnabled: true
    }
  });

  // Fetch profile image on component mount
  useEffect(() => {
    const fetchProfileImage = async () => {
      if (user?.email) {
        try {
          // Create a unique filename based on user email
          const filename = `${user.email.replace(/[^a-zA-Z0-9]/g, '_')}.png`;
          
          // Check if the file exists - Note the fixed code here
          const { data } = await supabase
            .storage
            .from('profiles')
            .getPublicUrl(`profile-images/${filename}`);
            
          if (data && data.publicUrl) {
            // Add a cache-busting parameter to ensure the browser reloads the image
            const cacheBuster = `?t=${new Date().getTime()}`;
            setProfileImageUrl(`${data.publicUrl}${cacheBuster}`);
          }
        } catch (error) {
          console.error("Error fetching profile image:", error);
        }
      }
    };
    
    // Initialize the storage bucket first
    const initStorage = async () => {
      try {
        await initializeStorage();
        fetchProfileImage();
      } catch (error) {
        console.error("Error initializing storage:", error);
      }
    };
    
    initStorage();
  }, [user]);

  const handleProfileSubmit = (data) => {
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      toast.success("Profilinformationen aktualisiert");
      setIsLoading(false);
    }, 1000);
  };

  const handleNotificationSubmit = (data) => {
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      toast.success("Benachrichtigungseinstellungen aktualisiert");
      setIsLoading(false);
    }, 1000);
  };

  const handleAdminSettingsSubmit = async (data) => {
    setIsLoading(true);
    
    // If storage is enabled, initialize the storage bucket
    if (data.storageEnabled) {
      try {
        await initializeStorage();
        toast.success("Speicher-Bucket initialisiert");
      } catch (error) {
        toast.error("Fehler beim Initialisieren des Speicher-Buckets");
        console.error(error);
      }
    }
    
    // Simulate API call for other settings
    setTimeout(() => {
      toast.success("Admin-Einstellungen aktualisiert");
      setIsLoading(false);
    }, 1000);
  };

  const resetStorageBucket = async () => {
    setIsLoading(true);
    try {
      await initializeStorage();
      toast.success("Speicher-Bucket zurückgesetzt");
    } catch (error) {
      toast.error("Fehler beim Zurücksetzen des Speicher-Buckets");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const uploadProfileImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }
    
    const file = event.target.files[0];
    const fileExt = file.name.split('.').pop();
    
    if (!user?.email) {
      toast.error("Benutzer-Email fehlt, Bild kann nicht hochgeladen werden");
      return;
    }
    
    // Create a unique filename based on user email
    const filename = `${user.email.replace(/[^a-zA-Z0-9]/g, '_')}.png`;
    
    setUploading(true);
    
    try {
      // Create the bucket if it doesn't exist
      await initializeStorage();
      
      // Upload the file
      const { data, error } = await supabase
        .storage
        .from('profiles')
        .upload(`profile-images/${filename}`, file, {
          cacheControl: '3600',
          upsert: true
        });
      
      if (error) {
        throw error;
      }
      
      // Get the public URL - No error property in this response
      const { data: urlData } = await supabase
        .storage
        .from('profiles')
        .getPublicUrl(`profile-images/${filename}`);
      
      // Add a cache-busting parameter
      const cacheBuster = `?t=${new Date().getTime()}`;
      setProfileImageUrl(`${urlData.publicUrl}${cacheBuster}`);
      
      toast.success("Profilbild erfolgreich aktualisiert");
    } catch (error) {
      console.error("Error uploading profile image:", error);
      toast.error("Fehler beim Hochladen des Profilbildes");
    } finally {
      setUploading(false);
    }
  };

  // Choose the appropriate layout based on user role
  const LayoutComponent = isAdmin ? AdminLayout : Layout;

  // Get initials for avatar fallback
  const getInitials = () => {
    if (user?.name) {
      return user.name.split(' ').map(n => n[0]).join('').toUpperCase();
    }
    return user?.email?.substring(0, 2).toUpperCase() || 'U';
  };

  return (
    <LayoutComponent>
      <div className="p-6">
        <h1 className="text-2xl font-semibold mb-6">Einstellungen</h1>
        
        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="profile">Profil</TabsTrigger>
            <TabsTrigger value="onboarding">Onboarding</TabsTrigger>
            <TabsTrigger value="notifications">Benachrichtigungen</TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="admin">Admin-Einstellungen</TabsTrigger>
            )}
          </TabsList>
          
          {/* Profile Settings */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>Profileinstellungen</CardTitle>
                <CardDescription>
                  Verwalten Sie Ihre persönlichen Informationen
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row gap-8 mb-8">
                  <div className="flex flex-col items-center">
                    <div className="relative group">
                      <Avatar className="h-24 w-24 border-2 border-primary">
                        <AvatarImage src={profileImageUrl || ''} alt={user?.name || 'Profilbild'} />
                        <AvatarFallback className="text-xl">{getInitials()}</AvatarFallback>
                      </Avatar>
                      <label 
                        htmlFor="profile-image" 
                        className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full 
                                  cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Upload className="text-white" size={24} />
                      </label>
                      <input 
                        type="file" 
                        id="profile-image" 
                        className="hidden" 
                        accept="image/*" 
                        onChange={uploadProfileImage}
                        disabled={uploading}
                      />
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                      {uploading ? 'Lädt hoch...' : 'Klicken zum Ändern'}
                    </p>
                  </div>
                  
                  <div className="flex-1">
                    <Form {...profileForm}>
                      <form onSubmit={profileForm.handleSubmit(handleProfileSubmit)} className="space-y-6">
                        <FormField
                          control={profileForm.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Name</FormLabel>
                              <FormControl>
                                <Input {...field} />
                              </FormControl>
                              <FormDescription>
                                Ihr vollständiger Name, wie er anderen Benutzern angezeigt wird.
                              </FormDescription>
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={profileForm.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>E-Mail</FormLabel>
                              <FormControl>
                                <Input {...field} type="email" />
                              </FormControl>
                              <FormDescription>
                                Ihre E-Mail-Adresse wird für Anmeldungen und Benachrichtigungen verwendet.
                              </FormDescription>
                            </FormItem>
                          )}
                        />
                        
                        <Button type="submit" disabled={isLoading}>
                          {isLoading ? "Speichern..." : "Speichern"}
                        </Button>
                      </form>
                    </Form>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Onboarding Tab */}
          <TabsContent value="onboarding">
            <OnboardingTab />
          </TabsContent>
          
          {/* Notification Settings */}
          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle>Benachrichtigungseinstellungen</CardTitle>
                <CardDescription>
                  Legen Sie fest, welche Benachrichtigungen Sie erhalten möchten
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...notificationForm}>
                  <form onSubmit={notificationForm.handleSubmit(handleNotificationSubmit)} className="space-y-6">
                    <FormField
                      control={notificationForm.control}
                      name="emailNotifications"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                          <div className="space-y-0.5">
                            <FormLabel className="text-base">E-Mail Benachrichtigungen</FormLabel>
                            <FormDescription>
                              Erhalten Sie Benachrichtigungen per E-Mail
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={notificationForm.control}
                      name="marketingEmails"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                          <div className="space-y-0.5">
                            <FormLabel className="text-base">Marketing E-Mails</FormLabel>
                            <FormDescription>
                              Erhalten Sie Marketing-E-Mails und Angebote
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={notificationForm.control}
                      name="newFeatureAlerts"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                          <div className="space-y-0.5">
                            <FormLabel className="text-base">Neue Funktionen</FormLabel>
                            <FormDescription>
                              Wir informieren Sie, wenn neue Funktionen verfügbar sind
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    
                    <Button type="submit" disabled={isLoading}>
                      {isLoading ? "Speichern..." : "Speichern"}
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Admin Settings (only for admin users) */}
          {isAdmin && (
            <TabsContent value="admin">
              <Card>
                <CardHeader>
                  <CardTitle>Admin-Einstellungen</CardTitle>
                  <CardDescription>
                    Systemeinstellungen, die nur für Administratoren verfügbar sind
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Form {...adminSettingsForm}>
                    <form onSubmit={adminSettingsForm.handleSubmit(handleAdminSettingsSubmit)} className="space-y-6">
                      <FormField
                        control={adminSettingsForm.control}
                        name="storageEnabled"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                            <div className="space-y-0.5">
                              <FormLabel className="text-base">Speicher aktiviert</FormLabel>
                              <FormDescription>
                                Erlaubt das Hochladen und Speichern von Dateien
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={adminSettingsForm.control}
                        name="maxUploadSizeMB"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Maximale Upload-Größe (MB)</FormLabel>
                            <FormControl>
                              <Input 
                                type="number" 
                                {...field} 
                                onChange={(e) => field.onChange(parseInt(e.target.value || "0", 10))}
                              />
                            </FormControl>
                            <FormDescription>
                              Die maximale Dateigröße für Uploads in Megabyte
                            </FormDescription>
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={adminSettingsForm.control}
                        name="aiGenerationEnabled"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                            <div className="space-y-0.5">
                              <FormLabel className="text-base">KI-Generierung aktiviert</FormLabel>
                              <FormDescription>
                                Erlaubt die Verwendung von KI für Vorschaubilder und Beschreibungen
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      
                      <div className="space-y-4">
                        <Button type="submit" disabled={isLoading}>
                          {isLoading ? "Speichern..." : "Einstellungen speichern"}
                        </Button>
                        
                        <div className="border-t pt-4 mt-4">
                          <h3 className="text-md font-medium mb-2">Speicher-Verwaltung</h3>
                          <p className="text-sm text-gray-400 mb-3">
                            Initialisieren oder setzen Sie den Speicher-Bucket zurück
                          </p>
                          <Button 
                            type="button" 
                            variant="outline" 
                            onClick={resetStorageBucket}
                            disabled={isLoading}
                            className="border-red-800 hover:bg-red-900/10"
                          >
                            Speicher-Bucket zurücksetzen
                          </Button>
                        </div>
                      </div>
                    </form>
                  </Form>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </LayoutComponent>
  );
};

export default Settings;
